import { Component } from 'preact';
import { TabBar } from './ui/TabBar';
import RuneSpinner from './RuneSpinner';
import { UI } from '../utils/arena/uiSounds';
import { getLocalApiOrigin } from '../utils/localApi';
import { t } from '../localization/t';
import {
  extractCuriosaDeckId,
  parseSorceryImportText,
  resolveSorceryDeckEntries,
} from '../utils/sorcery/importInput';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_BG, GOLD_BTN, BEVELED_BTN, INPUT_STYLE,
  DIALOG_STYLE, FourCorners,
} from '../lib/medievalTheme';

export default class DeckImportModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      urlInput: '',
      textInput: '',
      mode: 'url', // 'url' | 'text'
      loading: false,
      warnings: [],
      error: null,
    };
  }

  handleImportUrl = async () => {
    const deckId = extractCuriosaDeckId(this.state.urlInput.trim());
    if (!deckId) {
      this.setState({ error: t('Invalid Curiosa URL. Expected: sorcerytcg.com/decks/...') });
      return;
    }

    this.setState({ loading: true, error: null, warnings: [] });
    try {
      const api = getLocalApiOrigin();
      const res = await fetch(`${api}/api/curiosa/deck/${deckId}`);
      // The proxy always answers JSON. Anything else means the request
      // never reached it, so read the status rather than letting a
      // parse error surface as the user-facing message.
      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.success) {
        this.setState({ loading: false, error: data?.error || t('Failed to import deck ({status})', { status: res.status }) });
        return;
      }

      const { cards, warnings } = resolveSorceryDeckEntries(data, this.props.sorceryCards || []);
      if (cards.length === 0) {
        this.setState({ loading: false, error: t('No cards could be resolved') });
        return;
      }

      this.setState({ loading: false, warnings });
      this.props.onImport({ name: data.name || 'Imported Deck', cards, warnings });
    } catch (err) {
      this.setState({ loading: false, error: err.message || t('Failed to connect') });
    }
  };

  handleImportText = () => {
    const text = this.state.textInput.trim();
    if (!text) {
      this.setState({ error: t('Paste a deck list to import') });
      return;
    }

    try {
      const parsed = parseSorceryImportText(text);
      if (!parsed || (!parsed.cards?.length && !parsed.avatar)) {
        this.setState({ error: t('Could not parse any cards from the text') });
        return;
      }

      const { cards, warnings } = resolveSorceryDeckEntries(parsed, this.props.sorceryCards || []);
      if (cards.length === 0) {
        this.setState({ error: t('No cards could be resolved') });
        return;
      }

      this.props.onImport({ name: 'Imported Deck', cards, warnings });
    } catch (err) {
      this.setState({ error: err.message || t('Failed to parse deck list') });
    }
  };

  render() {
    const { onClose } = this.props;
    const { urlInput, textInput, mode, loading, error } = this.state;

    return (
      <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
        <div className="relative w-[27.5cqw]" style={{ ...DIALOG_STYLE, isolation: 'isolate' }}>
          <FourCorners />

          <div className="px-5 pt-5 pb-3">
            <h2 className="text-lg font-semibold arena-heading" style={{ color: TEXT_PRIMARY }}>{t('Import Deck')}</h2>
            <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>{t('Import from a Curiosa URL or paste a deck list.')}</p>
          </div>

          {/* Mode tabs */}
          <TabBar
            className="flex px-5 gap-2 mb-3"
            activeIndex={mode === 'text' ? 1 : 0}
            indicatorStyle={{ background: `${GOLD} 0.12)`, borderRadius: 'var(--radius-lg)', boxShadow: 'none' }}
          >
            <button
              type="button"
              data-tab-item
              className="flex-1 py-1.5 text-[0.6875cqw] font-semibold rounded-lg"
              style={{ color: mode === 'url' ? TEXT_PRIMARY : TEXT_MUTED }}
              onClick={() => this.setState({ mode: 'url', error: null })}
            >
              {t('Curiosa URL')}
            </button>
            <button
              type="button"
              data-tab-item
              className="flex-1 py-1.5 text-[0.6875cqw] font-semibold rounded-lg"
              style={{ color: mode === 'text' ? TEXT_PRIMARY : TEXT_MUTED }}
              onClick={() => this.setState({ mode: 'text', error: null })}
            >
              {t('Paste List')}
            </button>
          </TabBar>

          <div className="px-5 pb-5">
            {mode === 'url' ? (
              <input
                type="text"
                placeholder="https://sorcerytcg.com/decks/..."
                value={urlInput}
                onInput={(e) => this.setState({ urlInput: e.target.value, error: null })}
                className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                style={{ ...INPUT_STYLE, color: TEXT_BODY, backgroundColor: '#0e0a06' }}
              />
            ) : (
              <textarea
                rows={8}
                placeholder={t("Avatar: Card Name\n\nSpellbook:\n4 Card Name\n2 Another Card\n\nAtlas:\n2 Site Name")}
                value={textInput}
                onInput={(e) => this.setState({ textInput: e.target.value, error: null })}
                className="w-full px-3 py-2 text-sm rounded-lg outline-none resize-none"
                style={{ ...INPUT_STYLE, color: TEXT_BODY, backgroundColor: '#0e0a06' }}
              />
            )}

            {error && (
              <p className="text-xs mt-2" style={{ color: '#e89090' }}>{error}</p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-4 py-2 text-xs font-medium transition-all"
                style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                data-sound={UI.CANCEL}
                onClick={onClose}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="px-5 py-2 text-xs font-semibold transition-all"
                style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)', opacity: loading ? 0.6 : 1 }}
                data-sound={UI.CONFIRM}
                disabled={loading}
                onClick={mode === 'url' ? this.handleImportUrl : this.handleImportText}
              >
                {loading ? <RuneSpinner size={16} /> : t('Import')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
